import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import usePageTitle from '../../hooks/usePageTitle';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  Divider,
} from '@mui/material';

import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import Layout from '../../components/Layout';
import adminService from '../../services/adminService';
import { useUnion } from '../../contexts/UnionContext';

function UtilityTypesAdmin() {
  const { t } = useTranslation();
  usePageTitle(t('nav.utilityTypes'));
  const { activeUnion } = useUnion();
  const [utilityTypes, setUtilityTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedUtility, setSelectedUtility] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    calculation_method: 'FIXED_PER_APT',
    meter_scope: '',
    default_start_value: '',
    unit_price: '',
    unit: '',
    is_active: true,
    sort_order: 0,
  });

  // Import from bill state
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [selectedSuggestions, setSelectedSuggestions] = useState([]);
  const [detectedAddress, setDetectedAddress] = useState(null);
  const [savingSuggestions, setSavingSuggestions] = useState(false);

  // Drag-and-drop state
  const dragIndex = useRef(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const handleDragStart = (index) => {
    dragIndex.current = index;
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    setDragOverIndex(index);
  };

  const handleDrop = async (index) => {
    const from = dragIndex.current;
    if (from === null || from === index) {
      setDragOverIndex(null);
      return;
    }
    const reordered = [...utilityTypes];
    const [moved] = reordered.splice(from, 1);
    reordered.splice(index, 0, moved);
    // Assign new sort_order values (0-based sequential)
    const updated = reordered.map((u, i) => ({ ...u, sort_order: i }));
    setUtilityTypes(updated);
    dragIndex.current = null;
    setDragOverIndex(null);
    // Persist to backend
    try {
      await Promise.all(
        updated.map(u => adminService.updateUtilityType(u.id, { ...u, union: activeUnion.id }))
      );
    } catch (err) {
      console.error('Failed to save sort order:', err);
      setError(t('utilityTypes.failedSortOrder'));
      loadUtilityTypes(); // revert on failure
    }
  };

  const handleDragEnd = () => {
    setDragOverIndex(null);
  };

  const calculationMethods = [
    { value: 'FIXED_PER_APT', label: t('utilityTypes.fixedPerApt') },
    { value: 'PER_SQM', label: t('utilityTypes.perSqm') },
    { value: 'BY_CONSUMPTION', label: t('utilityTypes.byConsumption') },
    { value: 'BY_RESIDENTS', label: t('utilityTypes.byResidents') },
    { value: 'BY_OWNERSHIP_SHARE', label: t('utilityTypes.byOwnershipShare') },
  ];

  useEffect(() => {
    if (activeUnion) {
      loadUtilityTypes();
    }
  }, [activeUnion]);

  const loadUtilityTypes = async () => {
    try {
      setLoading(true);
      const data = await adminService.getUtilityTypes();
      const list = Array.isArray(data) ? data : data.results || [];
      setUtilityTypes(list);
      setError('');
    } catch (err) {
      console.error('Failed to load utility types:', err);
      setError(t('utilityTypes.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (utility = null) => {
    if (utility) {
      setSelectedUtility(utility);
      setFormData({
        name: utility.name,
        calculation_method: utility.calculation_method,
        meter_scope: utility.meter_scope || '',
        default_start_value: utility.default_start_value || '',
        unit_price: utility.unit_price || '',
        unit: utility.unit || '',
        is_active: utility.is_active,
        sort_order: utility.sort_order,
      });
    } else {
      setSelectedUtility(null);
      setFormData({
        name: '',
        calculation_method: 'FIXED_PER_APT',
        meter_scope: '',
        default_start_value: '',
        unit_price: '',
        unit: '',
        is_active: true,
        sort_order: 0,
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedUtility(null);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value });
  };

  const handleSubmit = async () => {
    try {
      const submitData = {
        ...formData,
        union: activeUnion.id,
        unit_price: formData.unit_price ? parseFloat(formData.unit_price) : null,
        meter_scope: formData.calculation_method === 'BY_CONSUMPTION' ? (formData.meter_scope || null) : null,
        default_start_value: formData.default_start_value ? parseFloat(formData.default_start_value) : null,
        sort_order: parseInt(formData.sort_order),
      };

      if (selectedUtility) {
        await adminService.updateUtilityType(selectedUtility.id, submitData);
      } else {
        await adminService.createUtilityType(submitData);
      }
      handleCloseDialog();
      loadUtilityTypes();
    } catch (err) {
      console.error('Failed to save utility type:', err);
      setError(t('utilityTypes.failedToSave'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('utilityTypes.deleteConfirm'))) return;

    try {
      await adminService.deleteUtilityType(id);
      loadUtilityTypes();
    } catch (err) {
      console.error('Failed to delete utility type:', err);
      setError(t('utilityTypes.failedToDelete'));
    }
  };

  const handleOpenImportDialog = () => {
    setImportFile(null);
    setImportError('');
    setSuggestions([]);
    setSelectedSuggestions([]);
    setDetectedAddress(null);
    setImportDialogOpen(true);
  };

  const handleImportUpload = async () => {
    if (!importFile) return;
    setImporting(true);
    setImportError('');
    setSuggestions([]);
    try {
      const data = await adminService.importUtilityTypesFromBill(importFile);
      const list = data.suggestions || [];
      setSuggestions(list);
      setSelectedSuggestions(list.map((_, i) => i)); // select all by default
      setDetectedAddress(data.building_address || null);
    } catch (err) {
      setImportError(err.response?.data?.error || 'Failed to process the PDF.');
    } finally {
      setImporting(false);
    }
  };

  const toggleSuggestion = (index) => {
    setSelectedSuggestions(prev =>
      prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]
    );
  };

  const handleSaveSuggestions = async () => {
    const toSave = suggestions.filter((_, i) => selectedSuggestions.includes(i));
    if (!toSave.length) return;
    setSavingSuggestions(true);
    setImportError('');
    try {
      const results = await Promise.allSettled(toSave.map(s =>
        adminService.createUtilityType({
          ...s,
          union: activeUnion.id,
          is_active: true,
          sort_order: 0,
        })
      ));

      // Silently ignore duplicates (unique constraint); surface any other errors
      const realFailures = results.filter(r => {
        if (r.status === 'fulfilled') return false;
        const data = r.reason?.response?.data;
        const msg = JSON.stringify(data || '');
        // Skip "unique set" constraint violations — type already exists
        return !msg.includes('unique set');
      });

      if (realFailures.length > 0) {
        setImportError(`${realFailures.length} type(s) could not be saved.`);
      } else {
        setImportDialogOpen(false);
      }
      loadUtilityTypes();
    } catch (err) {
      setImportError('Unexpected error while saving.');
    } finally {
      setSavingSuggestions(false);
    }
  };

  const getMethodLabel = (method) => {
    const found = calculationMethods.find(m => m.value === method);
    return found ? found.label : method;
  };

  if (!activeUnion) {
    return (
      <Layout>
        <Box sx={{ py: 4 }}>
          <Alert severity="warning">{t('members.selectUnion')}</Alert>
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box sx={{ py: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4">{t('utilityTypes.title')}</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" startIcon={<UploadFileIcon />} onClick={handleOpenImportDialog}>
              {t('utilityTypes.importFromBill')}
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
              {t('utilityTypes.addType')}
            </Button>
          </Box>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 40 }} />
                  <TableCell>{t('common.name')}</TableCell>
                  <TableCell>{t('utilityTypes.calcMethod')}</TableCell>
                  <TableCell align="right">{t('utilityTypes.unitPrice')}</TableCell>
                  <TableCell>{t('utilityTypes.unit')}</TableCell>
                  <TableCell align="center">{t('common.status')}</TableCell>
                  <TableCell align="right">{t('common.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {utilityTypes.map((utility, index) => (
                  <TableRow
                    key={utility.id}
                    hover
                    draggable
                    onDragStart={() => handleDragStart(index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDrop={() => handleDrop(index)}
                    onDragEnd={handleDragEnd}
                    sx={{
                      cursor: 'grab',
                      opacity: dragIndex.current === index ? 0.4 : 1,
                      backgroundColor: dragOverIndex === index ? 'action.hover' : undefined,
                      transition: 'background-color 0.15s',
                      '&:active': { cursor: 'grabbing' },
                    }}
                  >
                    <TableCell sx={{ color: 'text.disabled', py: 1 }}>
                      <DragIndicatorIcon fontSize="small" />
                    </TableCell>
                    <TableCell><strong>{utility.name}</strong></TableCell>
                    <TableCell><Typography variant="body2">{getMethodLabel(utility.calculation_method)}</Typography></TableCell>
                    <TableCell align="right">{utility.unit_price ? `€${utility.unit_price}` : '-'}</TableCell>
                    <TableCell>{utility.unit || '-'}</TableCell>
                    <TableCell align="center">
                      <Chip
                        label={utility.is_active ? t('common.active') : t('common.inactive')}
                        color={utility.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => handleOpenDialog(utility)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete(utility.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Import from Bill Dialog */}
        <Dialog open={importDialogOpen} onClose={() => setImportDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{t('utilityTypes.importTitle')}</DialogTitle>
          <DialogContent>
            {importError && <Alert severity="error" sx={{ mb: 2 }}>{importError}</Alert>}

            {/* Step 1 — file pick + upload */}
            {suggestions.length === 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t('utilityTypes.importDesc')}
                </Typography>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<UploadFileIcon />}
                  fullWidth
                  sx={{ mb: 1 }}
                >
                  {importFile ? importFile.name : t('utilityTypes.choosePdf')}
                  <input
                    type="file"
                    accept="application/pdf"
                    hidden
                    onChange={e => setImportFile(e.target.files[0] || null)}
                  />
                </Button>
              </Box>
            )}

            {/* Step 2 — review suggestions */}
            {suggestions.length > 0 && (
              <Box sx={{ mt: 1 }}>
                {detectedAddress && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    {t('utilityTypes.detectedBuilding', { address: detectedAddress })}
                  </Alert>
                )}
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {t('utilityTypes.foundTypes', { count: suggestions.length })}
                </Typography>
                <List dense disablePadding>
                  {suggestions.map((s, i) => (
                    <React.Fragment key={i}>
                      <ListItem disableGutters>
                        <Checkbox
                          edge="start"
                          checked={selectedSuggestions.includes(i)}
                          onChange={() => toggleSuggestion(i)}
                          size="small"
                        />
                        <ListItemText
                          primary={s.name}
                          secondary={`${getMethodLabel(s.calculation_method)}${s.calculation_method === 'BY_CONSUMPTION' && s.meter_scope ? ` · ${s.meter_scope === 'APARTMENT' ? t('utilityTypes.perApartment') : t('utilityTypes.wholeBuilding')}` : ''}${s.unit_price != null ? ` · €${s.unit_price}` : ''}${s.unit ? ` / ${s.unit}` : ''}`}
                        />
                      </ListItem>
                      {i < suggestions.length - 1 && <Divider component="li" />}
                    </React.Fragment>
                  ))}
                </List>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setImportDialogOpen(false)} disabled={importing || savingSuggestions}>
              {t('common.cancel')}
            </Button>
            {suggestions.length === 0 ? (
              <Button
                onClick={handleImportUpload}
                variant="contained"
                disabled={!importFile || importing}
                startIcon={importing ? <CircularProgress size={16} color="inherit" /> : null}
              >
                {importing ? t('utilityTypes.analysing') : t('utilityTypes.analysePdf')}
              </Button>
            ) : (
              <Button
                onClick={handleSaveSuggestions}
                variant="contained"
                disabled={selectedSuggestions.length === 0 || savingSuggestions}
                startIcon={savingSuggestions ? <CircularProgress size={16} color="inherit" /> : null}
              >
                {savingSuggestions ? t('common.saving') : t('utilityTypes.createTypes', { count: selectedSuggestions.length })}
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>{selectedUtility ? t('utilityTypes.editType') : t('utilityTypes.addType')}</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('common.name')}
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  helperText="e.g., 'Water', 'Heating', 'Maintenance'"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>{t('utilityTypes.calcMethod')}</InputLabel>
                  <Select
                    name="calculation_method"
                    value={formData.calculation_method}
                    onChange={handleChange}
                    label={t('utilityTypes.calcMethod')}
                  >
                    {calculationMethods.map((method) => (
                      <MenuItem key={method.value} value={method.value}>
                        {method.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {formData.calculation_method === 'BY_CONSUMPTION' && (
                <>
                  <Grid item xs={6}>
                    <FormControl fullWidth>
                      <InputLabel>{t('utilityTypes.meterScope')}</InputLabel>
                      <Select
                        name="meter_scope"
                        value={formData.meter_scope}
                        onChange={handleChange}
                        label={t('utilityTypes.meterScope')}
                      >
                        <MenuItem value="BUILDING">{t('utilityTypes.wholeBuilding')}</MenuItem>
                        <MenuItem value="APARTMENT">{t('utilityTypes.perApartment')}</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label={t('utilityTypes.defaultStartValue')}
                      name="default_start_value"
                      type="number"
                      inputProps={{ step: '0.001' }}
                      value={formData.default_start_value}
                      onChange={handleChange}
                      helperText={t('utilityTypes.defaultStartHelper')}
                    />
                  </Grid>
                </>
              )}
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label={t('utilityTypes.unitPrice')}
                  name="unit_price"
                  type="number"
                  inputProps={{ step: '0.01' }}
                  value={formData.unit_price}
                  onChange={handleChange}
                  helperText={t('utilityTypes.pricePerUnit')}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label={t('utilityTypes.unit')}
                  name="unit"
                  value={formData.unit}
                  onChange={handleChange}
                  helperText={t('utilityTypes.unitPlaceholder')}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label={t('utilityTypes.sortOrder')}
                  name="sort_order"
                  type="number"
                  value={formData.sort_order}
                  onChange={handleChange}
                  helperText={t('utilityTypes.displayOrder')}
                />
              </Grid>
              <Grid item xs={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.is_active}
                      onChange={handleChange}
                      name="is_active"
                    />
                  }
                  label={t('common.active')}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>{t('common.cancel')}</Button>
            <Button onClick={handleSubmit} variant="contained">{t('common.save')}</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
}

export default UtilityTypesAdmin;
